import React from "react";
import LegalLayout from "@/components/LegalLayout";

export default function PhotoContentLicense() {
  return (
    <LegalLayout
      slug="content-license"
      eyebrow="Content"
      title="Photo &amp; User Content License"
      lastUpdated="May 26, 2026 · Reviewed by counsel"
    >
      <p>
        When you upload photos, videos, kennel logos, dog descriptions,
        pedigree notes, journal posts, or any other content
        ("<strong>User Content</strong>") to Armstrong K9 Registry
        you grant us a license described below. This page exists so the
        scope of that license is clear and easy to find — it does not
        transfer ownership.
      </p>

      <h2>1. You own your content</h2>
      <p>
        You retain all copyright, trademark, and other intellectual-property
        rights in the User Content you upload. We do not claim ownership.
      </p>

      <h2>2. License you grant to us</h2>
      <p>
        You grant Armstrong K9 Registry a worldwide, royalty-free,
        non-exclusive, transferable, sublicensable license to host, store,
        reproduce, display, modify (for technical purposes such as
        thumbnailing, format conversion, or watermark stripping for accessible
        rendering), distribute, and publicly perform your User Content
        <strong> solely to operate the platform and provide the services you have signed up for</strong>.
      </p>
      <p>This license covers, for example:</p>
      <ul>
        <li>Showing your dog photos on the dog's profile page, your kennel storefront, the public marketplace, and the breed catalog.</li>
        <li>Generating thumbnails, OG images, and accessible alt-text from your photos.</li>
        <li>Generating PDFs (pedigree certificate, registration certificate, sitter-recap, vet-visit PDF) that embed your photos.</li>
        <li>Showing your kennel name, logo, and slug across marketplace + breeding directory listings.</li>
        <li>Backing up your content to durable storage so we can restore it if there is a hardware failure.</li>
      </ul>

      <h2>3. Limited promotional use</h2>
      <p>
        You also grant us permission to use a <strong>limited subset</strong> of
        public User Content in our own marketing materials, social posts,
        and demo videos. Specifically:
      </p>
      <ul>
        <li>Photos that you have already published to a public kennel storefront or public dog profile.</li>
        <li>Kennel names you have listed publicly.</li>
        <li>Breed-catalog stat blocks you have contributed.</li>
      </ul>
      <p>
        Private content — content visible only to you, your buyers, your
        sitters, or your designated vet — is <strong>never</strong> used
        promotionally without separate written permission.
      </p>
      <p>
        You can opt out of promotional use at any time by emailing the
        address in §10. Opt-out takes effect for new materials immediately;
        materials already printed, scheduled, or filed (e.g. a tax filing
        that includes a press release) cannot retroactively be redacted.
      </p>

      <h2>4. What you promise to us</h2>
      <p>By uploading User Content you represent and warrant that:</p>
      <ul>
        <li>You own it, or you have the necessary rights and permissions to grant the license in §2.</li>
        <li>It does not infringe any third party's copyright, trademark, right of publicity, or privacy right.</li>
        <li>It is not defamatory, harassing, threatening, hateful, sexually explicit, or unlawful.</li>
        <li>It is not malware, spam, or a phishing attempt.</li>
        <li>If the content depicts an identifiable third party (e.g., a co-breeder, a previous owner, a vet) you have their consent to upload, or the depiction qualifies under a recognized public-interest exception.</li>
      </ul>

      <h2>5. Pedigree / registration data</h2>
      <p>
        Pedigree records, registration numbers, microchip IDs, and ownership
        history submitted to the registry are part of the registry's data of
        record. Once registered, this data is treated as platform record
        data — you may continue to use, export, and reference it freely, but
        you cannot retroactively "delete" a registered pedigree because doing
        so would corrupt downstream owners' records.
      </p>
      <p>
        If you believe a pedigree record contains an error, use the in-app
        "Request revision" flow. Approved revisions are versioned with a
        full audit trail.
      </p>

      <h2>6. What we will NOT do</h2>
      <ul>
        <li>Sell your photos as stock imagery to third parties.</li>
        <li>License your kennel name or logo to a competitor of yours.</li>
        <li>Train any AI model on your User Content. (See the <a href="/ai-use">AI Use Disclosure</a>.)</li>
        <li>Use private content in marketing.</li>
      </ul>

      <h2>7. DMCA / takedown</h2>
      <p>
        If you believe content uploaded by another user infringes your
        copyright, send a DMCA notice to
        <a href="mailto:dmca@armstrongk9registry.com"> dmca@armstrongk9registry.com</a>{" "}
        with the following:
      </p>
      <ul>
        <li>Your contact information.</li>
        <li>A description of the copyrighted work you claim has been infringed.</li>
        <li>The URL on Armstrong K9 Registry where the infringing content lives.</li>
        <li>A statement under penalty of perjury that you are the rights-holder or authorized to act on behalf of the rights-holder, and that the use is not authorized by you.</li>
        <li>Your physical or electronic signature.</li>
      </ul>
      <p>
        We act on valid DMCA notices within <strong>5 business days</strong>{" "}
        and notify the uploader. Repeat infringers' accounts are suspended.
      </p>

      <h2>8. Termination of the license</h2>
      <p>
        If you delete a piece of User Content from your dog, storefront, or
        journal, the license in §2 ends for that content — except for the
        narrow carve-outs needed to keep registry records intact (e.g., a
        pedigree certificate already issued to a buyer remains valid).
      </p>
      <p>
        If you close your account, your private content is deleted within
        90 days. Public content tied to record-of-record data (registered
        pedigrees) is retained per §5.
      </p>

      <h2>9. Verified-Kennel photo requirements</h2>
      <p>
        Kennels seeking the <strong>Verified Kennel</strong> badge must
        upload photos that meet the criteria listed in the
        <a href="/seller-standards"> Seller Standards</a>. Submitting photos
        that violate §4 above (false rights / stock photos pretending to be
        your own dogs) is grounds for immediate badge revocation and
        suspension.
      </p>

      <h2>10. Contact</h2>
      <p>
        Rights and licensing:
        <a href="mailto:legal@armstrongk9registry.com"> legal@armstrongk9registry.com</a>.
        DMCA: <a href="mailto:dmca@armstrongk9registry.com">dmca@armstrongk9registry.com</a>.
        General: use our <a href="/contact">Contact Us</a> form.
      </p>
    </LegalLayout>
  );
}
